import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CalendarDays, Car, Ellipsis, House, Users } from 'lucide-react'

import { tabForPathname, TENANT_TABS, type TenantTab } from './tabs'

const TAB_ICONS: Record<TenantTab, typeof House> = {
  today: House,
  schedule: CalendarDays,
  customers: Users,
  vehicles: Car,
  more: Ellipsis,
}

/**
 * Bottom tab bar — the phone-first primary nav (DESIGN.md §4.4, §5).
 * Five equal tabs; active = ink text + a 16×3px saved-green indicator
 * (shape + color, never color alone — §2). Hidden at lg+ where the
 * SideRail takes over. Safe-area padded for the home indicator.
 */
export function TabBar() {
  const { t } = useTranslation('shell')
  const location = useLocation()
  const active = tabForPathname(location.pathname)

  return (
    <nav
      aria-label={t('tab.aria_label')}
      className="border-t border-border bg-background pb-[max(2px,env(safe-area-inset-bottom))] lg:hidden"
    >
      <div className="mx-auto flex max-w-2xl">
        {TENANT_TABS.map(({ key, to }) => {
          const Icon = TAB_ICONS[key]
          const isActive = active === key
          return (
            <Link
              key={key}
              to={to}
              aria-current={isActive ? 'page' : undefined}
              className={`flex h-[60px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors ${
                isActive ? 'text-foreground' : 'text-ink-3'
              }`}
            >
              <Icon className="size-[22px]" strokeWidth={2} />
              {t(`tab.${key}`)}
              <span
                aria-hidden="true"
                className={`mt-px h-[3px] w-4 rounded-[2px] ${
                  isActive ? 'bg-saved' : 'bg-transparent'
                }`}
              />
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
